<script>
	let name = "";
	let email = "";
	let topic = "general";
	let message = "";
	let loading = false;
	let success = false;
	let error = "";

	async function handleSubmit(e) {
		e.preventDefault();
		loading = true;
		success = false;
		error = "";

		try {
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name, email, topic, message }),
			});

			if (!response.ok) {
				throw new Error(
					"Unable to send your message right now. Please try again.",
				);
			}

			success = true;
			name = "";
			email = "";
			topic = "general";
			message = "";
		} catch (err) {
			error = err.message || "Something went wrong.";
		} finally {
			loading = false;
		}
	}
</script>

<svelte:head>
	<title>Contact | OS Studio</title>
	<meta
		name="description"
		content="Talk with the OS Studio team about platform design, deployment, and managed operations."
	/>
</svelte:head>

<section
	class="relative isolate min-h-screen overflow-hidden bg-gradient-to-br from-[#0b0716] via-[#241043] to-[#110922] text-white"
>
	<div class="absolute inset-0">
		<div
			class="absolute left-1/2 top-[-10rem] h-[30rem] w-[54rem] -translate-x-1/2 rounded-full bg-purple-500/30 blur-3xl"
		></div>
		<div
			class="absolute right-[-7rem] top-16 h-80 w-80 rounded-full bg-indigo-500/25 blur-3xl"
		></div>
		<div
			class="absolute bottom-[-8rem] left-[-6rem] h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl"
		></div>
	</div>
	<div
		class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:36px_36px] opacity-20"
	></div>

	<div class="relative container mx-auto max-w-7xl px-4 py-16 md:py-24">
		<div class="mb-12 max-w-3xl">
			<p
				class="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-purple-200"
			>
				Contact
			</p>
			<h1 class="mb-4 text-4xl font-semibold leading-tight md:text-6xl">
				Let us help you build and run your operating platform
			</h1>
			<p class="text-lg leading-relaxed text-purple-100">
				Share your goals and constraints. We will map the right platform
				path, timeline, and support model for your team.
			</p>
		</div>

		<div class="grid gap-7 lg:grid-cols-[1.05fr_0.95fr]">
			<div
				class="rounded-3xl border border-white/20 bg-white/10 p-7 backdrop-blur-xl sm:p-8"
			>
				<h2 class="mb-6 text-2xl font-semibold">Send a Message</h2>

				{#if success}
					<div
						class="mb-4 rounded-xl border border-emerald-300/40 bg-emerald-500/15 px-4 py-3 text-sm text-emerald-100"
					>
						Thanks. Your message was sent successfully. We usually
						reply within one business day.
					</div>
				{/if}

				{#if error}
					<div
						class="mb-4 rounded-xl border border-red-300/40 bg-red-500/15 px-4 py-3 text-sm text-red-100"
					>
						{error}
					</div>
				{/if}

				<form on:submit={handleSubmit} class="space-y-5">
					<div class="grid gap-5 sm:grid-cols-2">
						<div>
							<label
								for="name"
								class="mb-2 block text-sm font-semibold text-purple-100"
								>Full Name</label
							>
							<input
								id="name"
								type="text"
								bind:value={name}
								required
								placeholder="Your name"
								class="w-full rounded-xl border border-white/25 bg-slate-950/70 px-4 py-3 text-white placeholder:text-purple-200/70 focus:border-purple-300 focus:outline-none"
							/>
						</div>
						<div>
							<label
								for="email"
								class="mb-2 block text-sm font-semibold text-purple-100"
								>Work Email</label
							>
							<input
								id="email"
								type="email"
								bind:value={email}
								required
								placeholder="you@company.com"
								class="w-full rounded-xl border border-white/25 bg-slate-950/70 px-4 py-3 text-white placeholder:text-purple-200/70 focus:border-purple-300 focus:outline-none"
							/>
						</div>
					</div>

					<div>
						<label
							for="topic"
							class="mb-2 block text-sm font-semibold text-purple-100"
							>Topic</label
						>
						<select
							id="topic"
							bind:value={topic}
							class="w-full rounded-xl border border-white/25 bg-slate-950/70 px-4 py-3 text-white focus:border-purple-300 focus:outline-none"
						>
							<option value="general">General question</option>
							<option value="sales">Sales and pricing</option>
							<option value="deployment"
								>Deployment and architecture</option
							>
							<option value="support">Technical support</option>
						</select>
					</div>

					<div>
						<label
							for="message"
							class="mb-2 block text-sm font-semibold text-purple-100"
							>Message</label
						>
						<textarea
							id="message"
							bind:value={message}
							required
							rows="6"
							placeholder="Tell us what you are building and what help you need."
							class="w-full rounded-xl border border-white/25 bg-slate-950/70 px-4 py-3 text-white placeholder:text-purple-200/70 focus:border-purple-300 focus:outline-none"
						/>
					</div>

					<button
						type="submit"
						disabled={loading}
						class="w-full rounded-xl bg-primary px-6 py-3 font-semibold text-white transition-all hover:bg-primary-400 shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] disabled:opacity-50"
					>
						{loading ? "Sending..." : "Send Message"}
					</button>
				</form>
			</div>

			<div class="space-y-6">
				<div
					class="rounded-3xl border border-white/20 bg-white/10 p-6 backdrop-blur-xl"
				>
					<h3 class="mb-4 text-xl font-semibold">
						Talk to the right team
					</h3>
					<div class="space-y-4 text-sm">
						<div
							class="rounded-2xl border border-white/15 bg-slate-950/55 p-4"
						>
							<p class="mb-1 font-semibold">
								Sales and onboarding
							</p>
							<a
								href="mailto:sales@osstudio.ai"
								class="text-purple-200 hover:text-white"
								>sales@osstudio.ai</a
							>
						</div>
						<div
							class="rounded-2xl border border-white/15 bg-slate-950/55 p-4"
						>
							<p class="mb-1 font-semibold">Technical support</p>
							<a
								href="mailto:support@osstudio.ai"
								class="text-purple-200 hover:text-white"
								>support@osstudio.ai</a
							>
						</div>
						<div
							class="rounded-2xl border border-white/15 bg-slate-950/55 p-4"
						>
							<p class="mb-1 font-semibold">General inquiries</p>
							<a
								href="mailto:hello@osstudio.ai"
								class="text-purple-200 hover:text-white"
								>hello@osstudio.ai</a
							>
						</div>
					</div>
				</div>

				<div
					class="rounded-3xl border border-white/20 bg-white/10 p-6 backdrop-blur-xl"
				>
					<h3 class="mb-4 text-xl font-semibold">
						Response expectations
					</h3>
					<ul class="space-y-3 text-sm text-purple-100">
						<li>Business inquiries: within 1 business day</li>
						<li>
							Technical support: same day for active customers
						</li>
						<li>
							Architecture consultations: scheduled within 2-3
							days
						</li>
					</ul>
				</div>

				<div
					class="rounded-3xl border border-purple-300/40 bg-gradient-to-br from-purple-500/20 to-indigo-500/15 p-6"
				>
					<h3 class="mb-3 text-xl font-semibold">
						Need a guided start?
					</h3>
					<p class="mb-5 text-sm text-purple-100">
						Go through the start flow to share your requirements in
						structured format.
					</p>
					<a
						href="/start"
						class="inline-block rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-primary-400 shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] hover:text-white"
						>Open Start Flow</a
					>
				</div>
			</div>
		</div>
	</div>
</section>
